import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../services/toast.service';

interface ApiResponse<T = unknown> {
  isSuccess: boolean;
  statusCode: number;
  result: T | null;
  errors: unknown[];
  version: string;
  environment: string;
}

export const ForbiddenInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const router = inject(Router);
  const translateService = inject(TranslateService);

  const handle401Error = () => {
    toastService.error({ 
      summary: translateService.instant('errors.session_expired'), 
      detail: translateService.instant('errors.session_expired_message') 
    });
    // Clear storage and redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiration');
    localStorage.removeItem('userId');
    router.navigate(['/auth/login']);
  };

  const handle403Error = () => {
    toastService.error({ 
      summary: translateService.instant('errors.access_denied'), 
      detail: translateService.instant('errors.access_denied_message') 
    });
  };

  return next(req).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse)) return;
      
      if (event.body && typeof event.body === 'object' && !(event.body instanceof Blob)) {
        const body = event.body as Record<string, unknown>;
        if (Object.prototype.hasOwnProperty.call(body, 'statusCode')) {
          const apiBody = body as unknown as ApiResponse;

          if (apiBody.statusCode === 401) {
            handle401Error();
          } else if (apiBody.statusCode === 403) {
            handle403Error();
          }
        }
      }

      if (event.body instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const text = reader.result as string;
            const parsed = JSON.parse(text) as Partial<ApiResponse>;
            if (parsed && typeof parsed === 'object') {
              if (parsed.statusCode === 401) {
                handle401Error();
              } else if (parsed.statusCode === 403) {
                handle403Error();
              }
            }
          } catch {
            // Not JSON; ignore
          }
        };
        reader.readAsText(event.body);
      }
    }),
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          handle401Error();
        } else if (error.status === 403) {
          handle403Error();
        }
      }
      return throwError(() => error);
    })
  );
};

